import React, { useEffect, memo } from "react";
import { useSearchParams } from "react-router";
import { Column, Row } from "@/shared/ui/Blocks";
import { cn } from "@/shared/utils/shadcn";
import { IconButton } from "@/shared/ui/IconButton";

export interface BaseModalProps {
  header?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  closeOnTap?: boolean;
  onClose: () => void;
  onUpdate?: (
    meta: { [key: string]: any },
    modalStackProps?: { [key: string]: any }
  ) => void;
  classNames?: {
    root?: string;
    panel?: string;
    header?: string;
    content?: string;
    footer?: string;
  };
}

export const BaseModal = memo((props: BaseModalProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    header,
    footer,
    closeOnTap,
    children,
    classNames,
    onClose,
    onUpdate,
  } = props;

  const handleInsideClick: React.MouseEventHandler<HTMLDivElement> = (
    event
  ) => {
    if (!closeOnTap) {
      event.stopPropagation();
    }
  };

  const handleClose = () => {
    if (searchParams.get("modal")) {
      setSearchParams({});
    }
    onClose && onClose();
  };

  useEffect(() => {
    // Add class to body to disable scrolling
    document.body.classList.add("modal-open");

    // Add listener to close modal when escape key is pressed
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleEscapeKey);

    // Cleanup function to remove class from body
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [onClose]);

  return (
    <Column
      className={cn(
        "max-w-[752px] min-w-[350px] lg:min-w-[950px] min-h-[350px] space-y-4 p-12 bg-white border border-solid shadow-lg rounded-md border-grey-300 animate-scale-in",
        classNames?.panel
      )}
      style={{ maxHeight: "calc(100vh - 20%)" }}
      onClick={handleInsideClick}
    >
      {(!!header || !!onClose) && (
        <Row className={cn("space-x-8 justify-between", classNames?.header)}>
          <Column className="self-start w-full">{header}</Column>
          {!!onClose && (
            <IconButton
              variant="outline"
              icon="Close"
              color="black"
              iconProps={{ size: "md" }}
              onClick={handleClose}
              className="rounded-full p-0 self-start aspect-square"
            />
          )}
        </Row>
      )}
      <Column
        className={cn(
          "relative flex-1 w-full h-full overflow-y-auto",
          classNames?.content
        )}
      >
        {children}
      </Column>
      {footer && <Row className={`${classNames?.footer || ""}`}>{footer}</Row>}
    </Column>
  );
});
